import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogRef } from '@angular/material/dialog';
import { SuperAdminService } from '../../../services/super-admin/super-admin.service';
import { AdvanceSearchService } from '../../../services/Advance-search/advance-search.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';
import { FamilyMemberLookup } from '../../../common-files/family-member-lookup';
import { UsernameAvailability } from '../../../common-files/username-availability';

// create a user + assign one role (optionally at a branch). Super admin can assign
// ANY role at ANY branch. The person is picked by family code + name; the SSO uuid
// (mem_id) comes from the picked member and is never typed. self-saving.
@Component({
  selector: 'app-sa-add-user-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './sa-add-user-dialog.component.html',
  styleUrls: ['./sa-add-user-dialog.component.scss', '../../../../common-style/dropdown.scss', '../../../../common-style/ac-dialog.scss'],
})
export class SaAddUserDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  roles: any[] = [];
  establishments: any[] = [];
  isLoading: boolean = false;

  // family-member picker (fills fullName + memUuid)
  readonly memberLookup: FamilyMemberLookup;
  // live username check (debounced — fires when the admin pauses typing)
  readonly usernameCheck: UsernameAvailability;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<SaAddUserDialogComponent>,
    private superAdminService: SuperAdminService,
    private advanceSearchService: AdvanceSearchService,
    private toastService: ToastService
  ) {
    this.memberLookup = new FamilyMemberLookup(
      (fc) => this.advanceSearchService.fetchPhilDetails(fc),
      (memUuid) => this.advanceSearchService.checkMemberAvailability(memUuid)
    );
    this.usernameCheck = new UsernameAvailability((name) =>
      this.advanceSearchService.checkUsernameAvailability(name)
    );
    this.form = this.fb.group({
      userName: ['', [Validators.required, Validators.minLength(5)]],
      familyCode: ['', [Validators.required, Validators.pattern(/^\d{12}$/)]],
      // both are set by picking a name — never typed
      fullName: ['', Validators.required],
      memUuid: ['', Validators.required],
      roleKey: ['', Validators.required],
      validUntil: [''],
      establishmentId: [null],
      mobileNumber: [''],
      emailId: [''],
      identityNumber: [''],
    });
    this.usernameCheck.attach(this.form.get('userName')!);
    this.memberLookup.attach(
      this.form.get('familyCode')!,
      (message) => this.toastService.error(message),
      () => {}
    );
  }

  ngOnDestroy(): void {
    this.usernameCheck.destroy();
    this.memberLookup.destroy();
  }

  ngOnInit(): void {
    this.superAdminService.listRoles().subscribe({
      next: (res: any) => (this.roles = res?.data?.roles || []),
      error: () => {},
    });
    this.superAdminService.listEstablishments().subscribe({
      next: (res: any) => (this.establishments = res?.data?.establishments || []),
      error: () => {},
    });
  }

  // a changed family code invalidates the picked member
  onFamilyCodeInput(): void {
    this.memberLookup.reset();
    this.form.patchValue({ fullName: '', memUuid: '' });
  }

  // picking a name maps the SSO uuid internally and validates the person at
  // once — someone who already has an account is refused right here.
  onMemberPick(memUuid: string): void {
    this.form.patchValue({ fullName: this.memberLookup.fullNameOf(memUuid) });
    this.memberLookup.verifyPick(memUuid);
  }

  // a Volunteer is given for a limited time, so an expiry date is mandatory
  get isVolunteer(): boolean {
    return this.form.get('roleKey')?.value === 'ESTAB_VOLUNTEER';
  }

  submit(): void {
    if (this.form.invalid) {
      this.toastService.error('Username (5+ characters), family code, name and role are required.');
      return;
    }
    if (this.usernameCheck.status === 'taken') {
      this.toastService.error('This username is already taken.');
      return;
    }
    if (this.memberLookup.pickStatus === 'taken') {
      this.toastService.error('This person already has a user account.');
      return;
    }
    if (this.isVolunteer && !this.form.value.validUntil) {
      this.toastService.error('Volunteer role needs an expiry date.');
      return;
    }
    this.isLoading = true;
    const payload = {
      ...this.form.value,
      validUntil: this.isVolunteer ? this.form.value.validUntil : null,
    };
    this.superAdminService.createUser(payload).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'User created.');
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to create user.');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
